import { type ReactElement, type VFC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Measure from "react-measure";
import { shallowEqual, useSelector } from "react-redux";
import { useMatch } from "react-router";

import style from "./styles/BlastResult.module.scss";

import { type VerticalState, verticalState } from "../../app-utils/vertical-state.js";
import { type BlastHit, type BlastHsp } from "../../graphql.js";
import { type IAppState } from "../../redux/reducer.js";
import { STRAND_TYPE } from "../../settings/settings.js";
import { BlastResultContext } from "./BlastResultAlignmentsView.js";
import { ColoredSequence } from "./ColoredSequence.js";
import { type SequenceType } from "./SensorAlnInfoCard.js";

// constants for font: 14px, monospace
const CHAR_WIDTH_IN_PIXEL = 8.41;
const ALN_LINE_CHAR_INCREMENT = 20;

// See CSS: 45 + 20 + 70 + 20 + (auto) + 20 + 70.
// Card margins are out of the box model from the ResizeObserver.
const ALN_LABELS_WIDTH = 245;

export interface IPairwiseAlnProps {
  hit: BlastHit;
  hsp: BlastHsp;
  type: SequenceType;
}

interface ParamsFromRouter {
  hitNum?: string;
  hspNum?: string;
}

/**
 * Displays a pairwise sequence alignment (DNA or AA).
 * Performs automatic line breaking in intervals of length ALN_LINE_CHAR_INCREMENT.
 * Each alignment line consists of 3 instances of ColoredSequence.
 * Provides access to the underlying DOM nodes that represent an alignment row.
 */
const SensorPairwiseAln: VFC<IPairwiseAlnProps> = (props) => {
  const hitNum = useMatch("/job/:jobId/results/:queryId/alignments/:hitNum/*")?.params.hitNum;
  const hspNum = useMatch("/job/:jobId/results/:queryId/alignments/:hitNum/:hspNum")?.params.hspNum;

  const [toHit, toHsp] = useMemo(() => {
    return [hitNum ? Number.parseInt(hitNum, 10) : 1, hspNum ? Number.parseInt(hspNum, 10) : 1];
  }, [hitNum, hspNum]);

  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const context = useContext(BlastResultContext);

  const colorMismatchesOnly = useSelector<IAppState, boolean>((state) => state.blast.colorMismatchesOnly, shallowEqual);

  const scrollElement = useMemo(() => context.ref.current, [context.ref]);
  const [elementVerticalState, setElementVerticalState] = useState<VerticalState | null>(null);

  useEffect(() => {
    if (ref.current && scrollElement) {
      setElementVerticalState(verticalState(ref.current, scrollElement));
    }
  }, [scrollElement, context.scrollUpdate]);

  const createMismatchString = useCallback(
    (hSeq: string, qSeq: string, colorMismatchesOnly: boolean): string | null => {
      let mismatches: string | null = null;

      if (colorMismatchesOnly) {
        mismatches = "";
        let i = 0;
        while (i < qSeq.length) {
          mismatches += qSeq.charAt(i).toLowerCase() !== hSeq.charAt(i).toLowerCase() ? 1 : 0;
          i++;
        }
      }

      return mismatches;
    },
    [],
  );

  const alnRows = useMemo(() => {
    const { hsp, type } = props;

    // These are needed and if box.width is undefined it means that the DOM is not yet rendered by React.
    if (!hsp.querySequence || !hsp.midline || !hsp.targetSequence || !width) return [];

    const charsPerLine = (width - ALN_LABELS_WIDTH) / CHAR_WIDTH_IN_PIXEL;
    const chars =
      hsp.alignmentLength <= charsPerLine
        ? hsp.alignmentLength
        : Math.max(0, Math.floor(charsPerLine / ALN_LINE_CHAR_INCREMENT) * ALN_LINE_CHAR_INCREMENT);
    const ROWS = Math.ceil(hsp.querySequence.length / chars);
    const alnRows = new Array<ReactElement>(ROWS);
    let qPos = hsp.queryStart;
    let hPos = hsp.targetStart;

    for (let i = 0, rowCounter = 0; i < hsp.querySequence.length; i += chars, ++rowCounter) {
      const isVisible = elementVerticalState?.visible ?? false; // this.props.rowVisibility ? this.props.rowVisibility[rowCounter] : false;
      const end = Math.min(i + chars, hsp.querySequence.length);

      const qSeq = hsp.querySequence.substring(i, end);
      const mSeq = hsp.midline.substring(i, end);
      const hSeq = hsp.targetSequence.substring(i, end);

      const qResidueCount = qSeq.replace(/-/g, "").length;
      const hResidueCount = hSeq.replace(/-/g, "").length;

      const qRowEnd =
        !hsp.queryStrand || hsp.queryStrand === STRAND_TYPE.PLUS ? qPos + qResidueCount - 1 : qPos - qResidueCount + 1;
      const hRowEnd =
        !hsp.targetStrand || hsp.targetStrand === STRAND_TYPE.PLUS
          ? hPos + hResidueCount - 1
          : hPos - hResidueCount + 1;

      // if asked to only color mismatches, create a string of seq.length
      // composed from 0 (match) and 1 (mismatch) characters
      const mismatches = createMismatchString(hSeq, qSeq, colorMismatchesOnly);

      alnRows[rowCounter] = (
        <div className={style.aln_row} key={`aln-row-${props.hit.rank}-${hsp.rank}-${i}`}>
          <div className={style.aln_line}>
            <span style={{ userSelect: "none" }}>Query</span>
            <span style={{ userSelect: "none" }}>{qPos}</span>
            {isVisible && (
              <>
                <ColoredSequence
                  offset={qPos}
                  type={type}
                  seq={qSeq}
                  strand={hsp.queryStrand}
                  mismatches={mismatches}
                />
                <span style={{ userSelect: "none" }}>{qRowEnd}</span>
              </>
            )}
          </div>
          <div className={style.aln_line}>
            <span style={{ userSelect: "none" }}>&nbsp;</span>
            <span style={{ userSelect: "none" }}>&nbsp;</span>
            {isVisible && (
              <>
                <ColoredSequence offset={1} type={type} seq={mSeq} mismatches={mismatches} />
                <span style={{ userSelect: "none" }}>&nbsp;</span>
              </>
            )}
          </div>
          <div className={style.aln_line}>
            <span style={{ userSelect: "none" }}>Sbjct</span>
            <span style={{ userSelect: "none" }}>{hPos}</span>
            {isVisible && (
              <>
                <ColoredSequence
                  offset={hPos}
                  type={type}
                  seq={hSeq}
                  strand={hsp.targetStrand}
                  mismatches={mismatches}
                />
                <span style={{ userSelect: "none" }}>{hRowEnd}</span>
              </>
            )}
          </div>
        </div>
      );
      qPos = !hsp.queryStrand || hsp.queryStrand === STRAND_TYPE.PLUS ? qPos + qResidueCount : qPos - qResidueCount;
      hPos = !hsp.targetStrand || hsp.targetStrand === STRAND_TYPE.PLUS ? hPos + hResidueCount : hPos - hResidueCount;
    }

    return alnRows;
  }, [width, colorMismatchesOnly, createMismatchString, elementVerticalState?.visible, props]);

  useEffect(() => {
    if (scrollElement && ref.current && elementVerticalState) {
      if (toHit === 1 && toHsp === 1) {
        // Don't scroll for the first hsp
        return;
      }
      if (toHit === props.hit.rank && toHsp === props.hsp.rank) {
        // 130px is the approximate height of the Attributes List + Card Header.
        scrollElement?.scrollTo({ top: Math.max(elementVerticalState.topPosition - 130, 0) });
      }
    }
    // force to scroll again if any rendering changes the topPosition
  }, [elementVerticalState?.topPosition]);

  return (
    <Measure client onResize={({ client }) => setWidth(client?.width ?? 0)} innerRef={ref}>
      {({ measureRef }) => (
        <div className={style.pairwise_aln} ref={measureRef}>
          {alnRows}
        </div>
      )}
    </Measure>
  );
};

export { SensorPairwiseAln };
